import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { AbstractComponent } from '../../abstract.component';
import { SolViewHostComponent, SolViewHostData } from '../sol-view-host/sol-view-host.component';
import { SolValidationMessagesComponent } from '../../form/sol-validation-messages/sol-validation-messages.component';
import { SolButtonComponent } from '../../core/sol-button/sol-button.component';
import { NgTemplateOutlet } from '@angular/common';
import { SolIcon } from '@sol/icons';
import { SolButtonType } from '../../../models/core/sol-button';
import { SolType } from '../../../models/core/sol-type';
import { SolViewId } from '../../../models/core/sol-view-id';
import { SolViewOptions } from '../../../models/core/sol-view-options';
import { SolValidationMessage } from '../../../models/core/sol-validation-message';
import { SolViewType, ViewService } from '../../../services/view.service';
import { SolPath } from '../../../models/core/sol-path';
import { SolViewEvent } from '../abstract-view-component';
import { ObjectTools } from '../../../tools/object-tools';
import { SolArrayChange } from '../../../models/core/sol-array-change';

export interface SolViewData<V, O extends SolViewOptions> {
  dataType: SolType<V>;
  viewId?: SolViewId<O>;
  mode: SolViewType;
  value: V | undefined;
  viewOptions?: Partial<O>;
  messages?: SolValidationMessage[];
  immediate?: boolean;
  focusIndex?: number;
}

export interface SolViewFocusEvent {
  index: number;
  focused: boolean;
}

export interface SolViewActionEvent {
  index: number;
  actionId: string;
  payload: any;
}

/** A change the built-in array editor made: the whole new array value and what happened to it. */
export interface SolViewArrayValueEvent<V> {
  value: V;
  change: SolArrayChange;
}

interface SolViewItemHostData {
  index: number;
  hostData: SolViewHostData<any, any>;
  removable: boolean;
}

class SolViewState<V, O extends SolViewOptions> {
  private constructor(
    public readonly data: SolViewData<V, O> | undefined,
    public readonly showMessages: boolean,
    public readonly mode: SolViewType,
    public readonly hasArrayView: boolean,
    public readonly hostData: SolViewHostData<V, O> | undefined,
    public readonly itemHostDatas: SolViewItemHostData[],
    public readonly isArray: boolean,
    public readonly isArrayFallback: boolean,
    public readonly arrayFallbackType: SolType<any> | undefined,
  ) {}

  public static of<V, O extends SolViewOptions>(): SolViewState<V, O> {
    return new SolViewState<V, O>(undefined, true, 'read', false, undefined, [], false, false, undefined);
  }

  public withData(data: SolViewData<V, O> | undefined): SolViewState<V, O> {
    if (ObjectTools.equals(this.data, data)) {
      return this;
    }
    return this.copy({ data }).recalculateArrayFields();
  }

  public withShowMessages(showMessages: boolean): SolViewState<V, O> {
    if (this.showMessages === showMessages) {
      return this;
    }
    return this.copy({ showMessages });
  }

  public withValue(value: V): SolViewState<V, O> {
    if (this.data === undefined || ObjectTools.equals(this.data.value, value)) {
      return this;
    }
    return this.withData({
      ...this.data,
      value,
    });
  }

  public withViewRegistration(
    hasArrayView: boolean,
    hostData: SolViewHostData<V, O> | undefined,
    itemHostDatas: SolViewItemHostData[],
    isArrayFallback: boolean,
    arrayFallbackType: SolType<any> | undefined,
  ): SolViewState<V, O> {
    if (
      this.hasArrayView === hasArrayView &&
      ObjectTools.equals(this.hostData, hostData) &&
      ObjectTools.equals(this.itemHostDatas, itemHostDatas) &&
      this.isArrayFallback === isArrayFallback &&
      ObjectTools.equals(this.arrayFallbackType, arrayFallbackType)
    ) {
      return this;
    }
    return this.copy({
      hasArrayView,
      hostData,
      itemHostDatas,
      isArrayFallback,
      arrayFallbackType,
    });
  }

  public withClearedViewRegistration(): SolViewState<V, O> {
    return this.withViewRegistration(false, undefined, [], false, undefined);
  }

  private recalculateArrayFields(): SolViewState<V, O> {
    const mode = this.data?.mode ?? 'read';
    const isArray = this.data?.dataType.isArray || false;
    if (this.mode === mode && this.isArray === isArray) {
      return this;
    }
    return this.copy({ mode, isArray });
  }

  private copy(props: Partial<SolViewState<V, O>>): SolViewState<V, O> {
    return ObjectTools.copy<SolViewState<V, O>>(this, props);
  }
}

interface ViewContext {
  isArrayType: boolean;
  hasArrayView: boolean;
  useArrayFallback: boolean;
  arrayFallbackType?: SolType<any>;
}

@Component({
  selector: 'sol-view',
  imports: [SolViewHostComponent, SolValidationMessagesComponent, SolButtonComponent, NgTemplateOutlet],
  templateUrl: './sol-view.component.html',
})
export class SolViewComponent<V, O extends SolViewOptions> extends AbstractComponent<SolViewState<V, O>> {
  protected readonly SolIcon = SolIcon;
  protected readonly SolButtonType = SolButtonType;

  // Dependencies
  private readonly viewService = inject(ViewService);

  // Computed
  protected readonly data$ = this.compute(state => state.data);
  protected readonly showMessages$ = this.compute(state => state.showMessages);
  protected readonly isArray$ = this.compute(state => state.isArray);
  protected readonly mode$ = this.compute(state => state.mode);
  protected readonly hasArrayView$ = this.compute(state => state.hasArrayView);
  protected readonly hostData$ = this.compute(state => state.hostData);
  protected readonly itemHostDatas$ = this.compute(state => state.itemHostDatas);
  protected readonly canAdd$ = this.compute(
    state => (state.data?.viewOptions as Partial<SolViewOptions> | undefined)?.canAdd ?? true,
  );

  // Events
  @Output('value')
  private readonly valueEmitter = new EventEmitter<V>();
  @Output('focusChange')
  private readonly focusEmitter = new EventEmitter<SolViewFocusEvent>();
  @Output('action')
  private readonly actionEmitter = new EventEmitter<SolViewActionEvent>();
  /** When bound, the built-in array editor emits its changes here instead of on `value`. */
  @Output('arrayValue')
  private readonly arrayValueEmitter = new EventEmitter<SolViewArrayValueEvent<V>>();

  constructor() {
    super(SolViewState.of());

    this.watchComputed(
      state => state.data,
      () => this.updateViewRegistration(),
    );
  }

  @Input()
  public set data(data: SolViewData<V, O> | undefined) {
    this.update(state => state.withData(data));
  }

  @Input()
  public set showMessages(showMessages: boolean) {
    this.update(state => state.withShowMessages(showMessages));
  }

  protected onHostValue(value: V) {
    this.onValueChange(value);
  }

  protected onHostFocused(event: boolean, index: number) {
    this.focusEmitter.emit({ index, focused: event });
  }

  protected onHostAction(index: number, event: SolViewEvent) {
    this.actionEmitter.emit({
      index,
      actionId: event.actionId,
      payload: event.payload,
    } as SolViewActionEvent);
  }

  protected onItemValue(value: any, index: number) {
    const data = this.state.data;
    if (!data || !data.dataType.isArray) {
      this.valueEmitter.emit(value as V);
      return;
    }
    const current = (data.value as any[]) || [];
    const newValues = current.map((v, i) => (i === index ? value : v));
    this.onArrayValueChange(newValues as V, { type: 'item', index });
  }

  protected onRemove(index: number) {
    const data = this.state.data;
    if (!data || !data.dataType.isArray) {
      return;
    }
    const current = (data.value as any[]) || [];
    const newValues = current.filter((_, i) => i !== index);
    this.onArrayValueChange(newValues as V, { type: 'remove', index });
  }

  protected onAdd() {
    const data = this.state.data;
    if (!data || !data.dataType.isArray) {
      return;
    }
    const itemType = (data.dataType as any).itemType as SolType<any> | undefined;
    const newItem = itemType ? itemType.defaultValue() : undefined;
    const current = (data.value as any[]) || [];
    const newValues = [...current, newItem];
    this.onArrayValueChange(newValues as V, { type: 'add', index: current.length });
  }

  private onArrayValueChange(value: V, change: SolArrayChange) {
    if (!this.arrayValueEmitter.observed) {
      this.onValueChange(value);
      return;
    }
    this.update(state => state.withValue(value));
    this.arrayValueEmitter.emit({ value, change });
  }

  private onValueChange(value: V) {
    const isArrayFallback = this.state.isArrayFallback;
    // Unwrap array fallback values before emitting to consumers
    const emittedValue = isArrayFallback && Array.isArray(value) ? ((value as any[])[0] as V) : value;
    this.update(state => state.withValue(emittedValue));
    this.valueEmitter.emit(emittedValue);
  }

  private updateViewRegistration(): void {
    // Nothing to show
    const state = this.state;
    const data = state.data;
    if (data === undefined) {
      this.update(current => current.withClearedViewRegistration());
      return;
    }

    const viewContext = this.resolveViewContext(data);
    const hostData = this.buildHostData(data, viewContext);
    const itemHostDatas = this.buildItemHostDatas(data, viewContext, hostData);

    this.update(current =>
      current.withViewRegistration(
        viewContext.hasArrayView,
        hostData,
        itemHostDatas,
        viewContext.useArrayFallback,
        viewContext.arrayFallbackType,
      ),
    );
  }

  private static createHostData<V, O extends SolViewOptions>(data: SolViewData<V, O>): SolViewHostData<V, O> {
    return {
      dataType: data.dataType,
      viewId: data.viewId,
      mode: data.mode,
      value: data.value,
      viewOptions: data.viewOptions,
      messages: data.messages,
      immediate: data.immediate,
    };
  }

  private resolveViewContext(data: SolViewData<V, O>): ViewContext {
    const viewType: SolViewType = data.mode;
    const dataType = data.dataType;
    const baseViewExists = this.viewService.exists(dataType, viewType, data.viewId);
    const isArrayType = dataType.isArray;
    const hasArrayView = isArrayType && baseViewExists;
    const arrayFallbackType = !isArrayType ? dataType.array : undefined;
    const useArrayFallback =
      arrayFallbackType !== undefined &&
      !baseViewExists &&
      this.viewService.exists(arrayFallbackType, viewType, data.viewId);
    return {
      isArrayType,
      hasArrayView,
      useArrayFallback,
      arrayFallbackType,
    };
  }

  private buildHostData(data: SolViewData<V, O>, viewContext: ViewContext): SolViewHostData<V, O> {
    const baseHostData = SolViewComponent.createHostData(data);
    if (!viewContext.useArrayFallback) {
      return baseHostData;
    }
    return {
      ...baseHostData,
      dataType: (viewContext.arrayFallbackType ?? data.dataType.array) as SolType<any>,
      value: [data.value] as any,
    };
  }

  private buildItemHostDatas(
    data: SolViewData<V, O>,
    viewContext: ViewContext,
    hostData: SolViewHostData<V, O>,
  ): SolViewItemHostData[] {
    if (!viewContext.isArrayType || viewContext.hasArrayView) {
      return [];
    }

    const values = (data.value as any[]) || [];
    const itemType = (data.dataType as any).itemType as SolType<any> | undefined;
    const baseHostData: SolViewHostData<any, any> = {
      ...hostData,
      dataType: itemType ?? data.dataType,
    };
    const options: Partial<SolViewOptions> = data.viewOptions ?? {};
    const lockedItems = options.lockedItems ?? 0;
    const canRemove = (options.canRemove ?? true) && values.length > (options.minItems ?? 0);

    return values.map((value, index) => ({
      index,
      hostData: {
        ...baseHostData,
        value,
        viewOptions: index < lockedItems ? { ...baseHostData.viewOptions, disabled: true } : baseHostData.viewOptions,
        messages: this.filterMessagesForIndex(data.messages, index),
      },
      removable: canRemove && index >= lockedItems,
    }));
  }

  private filterMessagesForIndex(
    messages: SolValidationMessage[] | undefined,
    index: number,
  ): SolValidationMessage[] | undefined {
    if (!messages || messages.length === 0) {
      return undefined;
    }
    const filtered = messages.filter(message => message.path && SolPath.index(message.path) === index);
    return filtered.length === 0 ? undefined : filtered;
  }
}
